import React, { useCallback, useEffect, useState } from "react";
import { Plus, Copy, RotateCcw, Trash2, UserCog, Mail, Phone } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Avatar, AvatarFallback } from "./components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "./components/ui/dialog";
import { toast } from "sonner";
import api from "./api";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

export default function AdminTeachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });

  // Credential reveal dialog (shown once after create/reset)
  const [credDialog, setCredDialog] = useState(null); // { email, password, mode: 'created'|'reset' }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/teachers");
      setTeachers(data);
    } catch {
      toast.error("Could not load teachers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    try {
      const { data } = await api.post("/admin/teachers", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        password: form.password.trim() || null,
      });
      setTeachers([{ ...data.teacher }, ...teachers]);
      setAddOpen(false);
      setForm({ name: "", email: "", phone: "", password: "" });
      setCredDialog({ email: data.teacher.email, password: data.temporary_password, mode: "created", name: data.teacher.name });
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Could not create teacher.";
      toast.error(typeof msg === "string" ? msg : "Could not create teacher.");
    }
  };

  const handleReset = async (t) => {
    if (!window.confirm(`Reset password for ${t.name}? Their old password will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/teachers/${t.id}/reset`);
      setCredDialog({ email: t.email, password: data.temporary_password, mode: "reset", name: t.name });
    } catch {
      toast.error("Reset failed.");
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete teacher ${t.name} and ALL their students, invoices, and reminders? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/teachers/${t.id}`);
      setTeachers(teachers.filter((x) => x.id !== t.id));
      toast.success(`Teacher ${t.name} removed.`);
    } catch {
      toast.error("Delete failed.");
    }
  };

  const copyToClipboard = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard.");
  };

  return (
    <div className="space-y-6" data-testid="admin-teachers-view">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Teacher Accounts</h2>
          <p className="text-xs text-slate-500 mt-1">Create teacher logins. Each teacher only sees their own students, attendance, fees, and reminders.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold h-10 px-4 gap-2" data-testid="add-teacher-btn">
          <Plus className="w-4 h-4" /> Create Teacher
        </Button>
      </div>

      <Card className="border border-slate-200 shadow-xs bg-white rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">Teachers ({teachers.length})</h3>
          <Badge variant="outline" className="border-indigo-200 text-indigo-600 bg-indigo-50">Live</Badge>
        </div>

        {loading ? (
          <div className="p-10 text-center text-xs text-slate-400">Loading teachers…</div>
        ) : teachers.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-teachers">
            <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 mx-auto flex items-center justify-center mb-4">
              <UserCog className="w-7 h-7" />
            </div>
            <p className="text-sm font-semibold text-slate-900">No teachers yet.</p>
            <p className="text-xs text-slate-500 mt-1">Click Create Teacher to add your first one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <th className="py-3 px-6">Teacher</th>
                  <th className="py-3 px-6">Contact</th>
                  <th className="py-3 px-6">Students</th>
                  <th className="py-3 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {teachers.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`teacher-row-${t.id}`}>
                    <td className="py-4 px-6 flex items-center gap-3">
                      <Avatar className="h-10 w-10 border border-slate-200">
                        <AvatarFallback className="bg-indigo-100 text-indigo-700 font-bold text-xs">
                          {t.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-bold text-slate-900">{t.name}</p>
                        <p className="text-xs text-slate-400 font-mono">{t.id.slice(0, 8)}…</p>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-xs text-slate-600">
                      <div className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-400" /> {t.email}</div>
                      {t.phone && <div className="flex items-center gap-1.5 mt-1"><Phone className="w-3 h-3 text-slate-400" /> {t.phone}</div>}
                    </td>
                    <td className="py-4 px-6 font-bold text-slate-900">{t.student_count}</td>
                    <td className="py-4 px-6 text-right space-x-2">
                      <Button size="sm" variant="outline"
                        onClick={() => handleReset(t)}
                        className="h-8 border-amber-200 text-amber-700 hover:bg-amber-50 text-xs font-medium gap-1"
                        data-testid={`reset-teacher-${t.id}`}>
                        <RotateCcw className="w-3 h-3" /> Reset Password
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => handleDelete(t)}
                        className="h-8 border-rose-200 text-rose-700 hover:bg-rose-50 text-xs font-medium gap-1"
                        data-testid={`delete-teacher-${t.id}`}>
                        <Trash2 className="w-3 h-3" /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* CREATE TEACHER MODAL */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl border-slate-200 shadow-2xl p-6 bg-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">Create Teacher Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <Field label="Full Name">
              <Input required placeholder="e.g. Ms. Clara Wren" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="border-slate-200 text-sm h-10" data-testid="new-teacher-name" />
            </Field>
            <Field label="Email">
              <Input required type="email" placeholder="clara@melodyteacher.com" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="border-slate-200 text-sm h-10" data-testid="new-teacher-email" />
            </Field>
            <Field label="Phone (optional)">
              <Input placeholder="+91 90000 00000" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="border-slate-200 text-sm h-10" data-testid="new-teacher-phone" />
            </Field>
            <Field label="Temporary Password (optional — leave blank to auto-generate)">
              <Input placeholder="Auto-generate if empty" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="border-slate-200 text-sm h-10 font-mono" data-testid="new-teacher-password" />
            </Field>
            <DialogFooter className="pt-4 border-t border-slate-100 flex gap-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className="text-xs font-medium">Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-5" data-testid="submit-new-teacher">Create Teacher</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CREDENTIAL REVEAL DIALOG */}
      <Dialog open={!!credDialog} onOpenChange={(v) => !v && setCredDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl border-slate-200 shadow-2xl p-6 bg-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900" data-testid="cred-dialog-title">
              {credDialog?.mode === "created" ? "Teacher created — share these credentials" : "Password reset — share the new password"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-slate-500">
              This password is shown <b>only once</b>. Copy it and share with {credDialog?.name} via a safe channel (WhatsApp, in-person, secure email). You can regenerate it any time.
            </p>
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50 space-y-3">
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Email</p>
                <div className="flex items-center justify-between mt-1">
                  <code className="text-sm font-mono text-slate-900" data-testid="cred-email">{credDialog?.email}</code>
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(credDialog.email)} className="h-7 gap-1 text-xs">
                    <Copy className="w-3 h-3" /> Copy
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Temporary Password</p>
                <div className="flex items-center justify-between mt-1">
                  <code className="text-sm font-mono text-indigo-700 font-bold" data-testid="cred-password">{credDialog?.password}</code>
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(credDialog.password)} className="h-7 gap-1 text-xs">
                    <Copy className="w-3 h-3" /> Copy
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="pt-4 border-t border-slate-100 mt-4">
            <Button onClick={() => setCredDialog(null)} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-5" data-testid="cred-dialog-close">
              I've saved it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
